'use client';


import { useMemo, useState } from 'react';
import {
  calculateRoomModes,
  calculateSchroederFrequency,
  type RoomMode,
} from '@/lib/acousticsPhysics';
import { toolFreqGrid } from '@/lib/tools/sbir';
import { buildNamedRoomStates, flatMagnitude } from '@/lib/tools/syntheticAcoustics';
import { computeModalSeatResponse } from '@/lib/tools/modalPressure';
import type { LengthUnit } from '@/lib/tools/units';
import { CONTENT, CONTENT_NAME, SECOND_LINE } from '@/components/seo/roles';
import { optionChipClass } from '@/components/room/chrome/selection';
import UnitToggle from '@/components/tools/UnitToggle';
import ToolDimSlider from '@/components/tools/ToolDimSlider';
import ToolMetricCard from '@/components/tools/ToolMetricCard';
import ToolLeadIn from '@/components/tools/ToolLeadIn';
import ModeSpectrum from '@/components/tools/ModeSpectrum';
import ToolFreqChart from '@/components/tools/ToolFreqChart';
import { ToolMultiAuralization } from '@/components/tools/ToolAuralization';

interface WidgetProps {
  initialLengthM: number;
  initialWidthM: number;
  initialHeightM: number;
  initialModes: RoomMode[];
  initialSchroederHz: number;
}

export default function RoomModeCalculatorWidget({
  initialLengthM,
  initialWidthM,
  initialHeightM,
}: WidgetProps) {
  const [unit, setUnit] = useState<LengthUnit>('m');
  const [lengthM, setLengthM] = useState(initialLengthM);
  const [widthM, setWidthM] = useState(initialWidthM);
  const [heightM, setHeightM] = useState(initialHeightM);
  const [filterType, setFilterType] = useState<'all' | 'axial' | 'tangential' | 'oblique'>('all');
  const [selectedHz, setSelectedHz] = useState<number | null>(null);

  const modes = useMemo(
    () => calculateRoomModes(lengthM, widthM, heightM, 250),
    [lengthM, widthM, heightM],
  );
  const volumeM3 = Math.round(lengthM * widthM * heightM * 10) / 10;
  const schroederHz = useMemo(() => calculateSchroederFrequency(volumeM3, 0.4), [volumeM3]);

  const filteredModes = useMemo(() => {
    if (filterType === 'all') return modes;
    return modes.filter((m) => m.type === filterType);
  }, [modes, filterType]);

  const axialModesCount = modes.filter((m) => m.type === 'axial').length;
  const tangentialModesCount = modes.filter((m) => m.type === 'tangential').length;
  const obliqueModesCount = modes.filter((m) => m.type === 'oblique').length;

  const freqs = useMemo(() => toolFreqGrid(20, 250, 140), []);
  const seat = useMemo(
    () =>
      computeModalSeatResponse(
        freqs,
        { lengthM, widthM, heightM },
        { x: Math.min(1.2, lengthM * 0.25), y: widthM * 0.3, z: 1.2 },
        { x: lengthM * 0.38, y: widthM / 2, z: 1.2 },
      ),
    [freqs, lengthM, widthM, heightM],
  );
  const magPure = useMemo(() => flatMagnitude(freqs), [freqs]);
  const audioStates = useMemo(
    () =>
      buildNamedRoomStates(freqs, [
        { id: 'pure', mag: magPure, nProducts: 0, rt60FineSec: 0.22, dryBroadband: true },
        { id: 'room', mag: [...seat.magAudio], nProducts: 0, rt60FineSec: 0.55, dryBroadband: false },
      ]),
    [freqs, magPure, seat],
  );
  const modeTicks = useMemo(
    () => modes.map((m) => ({ hz: m.freqHz, emphasis: m.type })),
    [modes],
  );

  const selectMode = (m: RoomMode) => {
    setSelectedHz(m.freqHz);
    setFilterType(m.type);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <UnitToggle unit={unit} onChange={setUnit} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pb-6 border-b border-[var(--app-border)]">
        <ToolDimSlider
          label="Room length (L)"
          valueM={lengthM}
          minM={2}
          maxM={12}
          stepM={0.1}
          unit={unit}
          onChangeM={setLengthM}
          testId="dim-length"
        />
        <ToolDimSlider
          label="Room width (W)"
          valueM={widthM}
          minM={2}
          maxM={10}
          stepM={0.1}
          unit={unit}
          onChangeM={setWidthM}
          testId="dim-width"
        />
        <ToolDimSlider
          label="Room height (H)"
          valueM={heightM}
          minM={2}
          maxM={6}
          stepM={0.1}
          unit={unit}
          onChangeM={setHeightM}
          testId="dim-height"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="room-mode-stats">
        <ToolMetricCard testId="stat-volume" label="Room volume" value={`${volumeM3} m³`} />
        <ToolMetricCard testId="stat-schroeder" label="Schroeder (f_s)" value={`~${schroederHz} Hz`} emphasized />
        <ToolMetricCard
          testId="stat-lowest"
          label="Lowest mode"
          value={modes[0] ? `${modes[0].freqHz} Hz` : 'N/A'}
        />
        <ToolMetricCard testId="stat-total" label="Total modes (<250Hz)" value={`${modes.length}`} />
      </div>

      <ModeSpectrum
        modes={modes}
        schroederHz={schroederHz}
        filterType={filterType}
        selectedHz={selectedHz}
        onSelectMode={selectMode}
      />

      <div className="space-y-2" data-testid="seat-response">
        <div>
          <h3 className={CONTENT_NAME}>Typical seat response</h3>
          <p className={`mt-0.5 ${SECOND_LINE}`}>
            All modes ≤250 Hz · relative dB · speaker ~25% depth, seat at 38% rule
          </p>
        </div>
        <ToolFreqChart
          freqs={freqs}
          curves={[
            { mag: magPure, tone: 'reference', label: 'Pure' },
            { mag: seat.mag, tone: 'accent', label: 'In room' },
          ]}
          relative
          dbRange={{ min: -24, max: 18 }}
          modeTicks={modeTicks}
          markerHz={selectedHz ?? undefined}
          markerLabel={selectedHz != null ? `${selectedHz.toFixed(0)} Hz` : undefined}
          fMax={250}
        />
        <ToolMultiAuralization
          states={audioStates}
          stateButtons={[
            { id: 'pure', label: 'Pure' },
            { id: 'room', label: 'In room' },
          ]}
          defaultState="room"
        />
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {(
            [
              ['all', `All (${modes.length})`],
              ['axial', `Axial (${axialModesCount})`],
              ['tangential', `Tangential (${tangentialModesCount})`],
              ['oblique', `Oblique (${obliqueModesCount})`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              data-testid={`mode-filter-${key}`}
              onClick={() => {
                setFilterType(key);
                if (key === 'all') setSelectedHz(null);
              }}
              // SELECTION IS NOT AN ACTION: a filter chip reads as raised when
              // chosen, never accent-filled — the accent stays on the chart's
              // curves and the Schroeder readout (AGENTS.md Button + selection
              // rule; this exact accent-on-both ambiguity is S2 in the audit).
              className={optionChipClass(filterType === key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="max-h-60 overflow-y-auto border border-[var(--app-border)] rounded-lg">
          <table className={`w-full text-left ${CONTENT}`} data-testid="mode-table">
            <thead className={`bg-[var(--app-bg)] ${SECOND_LINE} sticky top-0 border-b border-[var(--app-border)]`}>
              <tr>
                <th className="p-2.5">Frequency</th>
                <th className="p-2.5">Mode (nx, ny, nz)</th>
                <th className="p-2.5">Type</th>
                <th className="p-2.5">Relative energy</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--app-border)] bg-[var(--app-card)]">
              {filteredModes.slice(0, 30).map((m, i) => {
                const selected = selectedHz != null && Math.abs(m.freqHz - selectedHz) < 0.05;
                return (
                  <tr
                    key={`${m.label}-${i}`}
                    tabIndex={0}
                    onClick={() => selectMode(m)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        selectMode(m);
                      }
                    }}
                    className={
                      'cursor-pointer transition-colors ' +
                      (selected ? 'bg-[color-mix(in_oklch,var(--rt-accent)_15%,transparent)]' : 'hover:bg-[var(--app-hover)]')
                    }
                  >
                    <td className="p-2.5 font-mono text-[var(--rt-accent)] font-bold">{m.freqHz} Hz</td>
                    <td className="p-2.5 font-mono text-[var(--rt-ink)]">{m.label}</td>
                    <td className="p-2.5 capitalize">{m.type}</td>
                    <td className="p-2.5 text-[var(--rt-ink-dim)]">
                      {m.type === 'axial'
                        ? '100% (High)'
                        : m.type === 'tangential'
                          ? '50% (Medium)'
                          : '25% (Low)'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ToolLeadIn
        result={`${modes.length} modes below 250 Hz, Schroeder crossover at ~${schroederHz} Hz.`}
        ctaLabel="Launch 3D room solver"
        seed={{ w: widthM, l: lengthM, h: heightM, units: 'm' }}
      />
    </div>
  );
}
